import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { findCategoryMerged, findSubMerged } from "../lib/dynamicCatalog";
import ProductListings from "../components/ProductListings";
import { ArrowLeft, ArrowRight } from "lucide-react";

const CategoryPage = () => {
    const { category, sub } = useParams();
    const cat = findCategoryMerged(category);

    if (!cat) return <Navigate to="/" replace />;

    const subItem = sub ? findSubMerged(category, sub) : null;
    if (sub && !subItem) return <Navigate to={`/${category}`} replace />;

    // SUB-CATEGORY PAGE: minimal header, then listings immediately.
    if (sub && subItem) {
        return (
            <div data-testid={`category-page-${category}-${sub}`}>
                <section className="border-b border-white/10">
                    <div className="container-x py-8 md:py-10">
                        <Link
                            to={`/${cat.slug}`}
                            data-testid="back-to-category"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt mb-3 hover:gap-3 transition-all"
                        >
                            <ArrowLeft className="w-4 h-4" /> {cat.name}
                        </Link>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl text-white leading-none">
                            {subItem.name}
                        </h1>
                    </div>
                </section>
                <ProductListings
                    category={category}
                    subcategory={sub}
                    headerTitle={`${cat.name} · ${subItem.name}`}
                    headerSub={null}
                />
            </div>
        );
    }

    // CATEGORY LANDING PAGE: sub-category picker grid.
    return (
        <div data-testid={`category-page-${category}`}>
            <section className="border-b border-white/10">
                <div className="container-x py-10 md:py-14">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl text-white leading-none">
                        <span className="text-storm-bolt">DangerSky</span> {cat.name}
                    </h1>
                </div>
            </section>

            {cat.sub.length > 0 ? (
                <section className="container-x py-10 md:py-14">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {cat.sub.map((s) => (
                            <Link
                                key={s.slug}
                                to={`/${cat.slug}/${s.slug}`}
                                data-testid={`sub-tile-${s.slug}`}
                                className="group relative overflow-hidden aspect-[4/3] border border-white/10 hover:border-storm-bolt/50 transition-colors"
                            >
                                {s.image ? (
                                    <img
                                        src={s.image}
                                        alt={s.name}
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                ) : (
                                    <div className="absolute inset-0 bg-gradient-to-br from-storm-navy to-storm-void" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-storm-void via-storm-void/40 to-transparent" />
                                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 flex items-end justify-between gap-3">
                                    <div>
                                        <div className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-white">
                                            {s.name}
                                        </div>
                                        {s.blurb && (
                                            <div className="text-xs text-storm-mist mt-1">{s.blurb}</div>
                                        )}
                                    </div>
                                    <ArrowRight className="w-5 h-5 text-storm-bolt group-hover:translate-x-1 transition-transform shrink-0" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            ) : (
                <ProductListings
                    category={category}
                    subcategory={null}
                    headerTitle={cat.name}
                    headerSub={null}
                />
            )}
        </div>
    );
};

export default CategoryPage;
